// src/resources/commentReports/CommentReportList.js
import * as React from 'react';
import { List, Datagrid, TextField, DateField, FunctionField, SelectInput } from 'react-admin';
import { Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import ResourceStatusHeader from '../../components/ResourceStatusHeader';
import StatusField from '../../components/StatusField';
import { useResourceSearchFilter } from '../../hooks/useResourceSearch';
import {
    COMMENT_REPORT_STATUS_CONFIG,
    COMMENT_REPORT_STATUS_CHOICES,
} from './constants';
import CommentReportActionsField from './CommentReportActionsField';
import {
    getCommentAuthor,
    getCommentAuthorAvatar,
    getCommentContent,
    getReasonStats,
    getReportCount,
    getReporterList,
    getRdfMeta,
    truncate,
} from './helpers';

const CommentReportFilters = [
    <SelectInput
        key="status"
        source="status"
        label="Statut"
        alwaysOn
        choices={COMMENT_REPORT_STATUS_CHOICES}
        parse={(value) => (value === '' ? undefined : value)}
    />,
];

const renderCommentCell = (record) => {
    if (!record) return '—';
    const author = getCommentAuthor(record);
    const avatarUrl = getCommentAuthorAvatar(record);
    const content = truncate(getCommentContent(record), 140) || '—';
    const rdf = getRdfMeta(record);

    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Avatar src={avatarUrl || undefined}>{author?.slice(0, 2)?.toUpperCase()}</Avatar>
            <Box>
                <Typography variant="subtitle2">{author}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {content}
                </Typography>
                {rdf?.slug && (
                    <Typography variant="caption" color="text.secondary">
                        RDF : {rdf.slug}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
};

const renderReportsCell = (record) => {
    if (!record) return '—';
    const reasonStats = getReasonStats(record);
    const total = getReportCount(record);
    const reporters = getReporterList(record);

    return (
        <Box sx={{ maxWidth: 320 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {total || reasonStats.length || 1} signalement(s)
            </Typography>
            {reporters.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                    par {reporters.map((user) => user?.username).filter(Boolean).join(', ')}
                </Typography>
            )}
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {reasonStats.slice(0, 3).map((item) => (
                    <Chip
                        key={`${item.reason}-${item.count}`}
                        size="small"
                        color="default"
                        label={`${item.reason || 'Motif inconnu'} · ${item.count ?? 1}`}
                    />
                ))}
                {reasonStats.length === 0 && (
                    <Chip size="small" variant="outlined" label="Motif non renseigné" />
                )}
            </Stack>
        </Box>
    );
};

export default function CommentReportList() {
    const searchFilter = useResourceSearchFilter('comment-reports');

    return (
        <>
            <ResourceStatusHeader
                resource="comment-reports"
                title="Signalements de commentaires"
                subtitle="File de modération des commentaires publics"
                statusesConfig={COMMENT_REPORT_STATUS_CONFIG}
            />
            <List
                resource="comment-reports"
                exporter={false}
                perPage={25}
                sort={{ field: 'createdAt', order: 'DESC' }}
                filters={CommentReportFilters}
                filter={searchFilter}
                title="Signalements de commentaires"
            >
                <Datagrid rowClick="show" bulkActionButtons={false}>
                    <TextField source="id" label="ID" />
                    <FunctionField label="Commentaire" render={renderCommentCell} />
                    <FunctionField label="Signalements" render={renderReportsCell} />
                    <StatusField source="status" />
                    <DateField source="createdAt" label="Créé" showTime />
                    <DateField source="updatedAt" label="Mis à jour" showTime />
                    <CommentReportActionsField />
                </Datagrid>
            </List>
        </>
    );
}
